import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  allowUnverified?: boolean; // Allow access even if profile is incomplete or rejected
}

export default function ProtectedRoute({ children, adminOnly = false, allowUnverified = false }: ProtectedRouteProps) {
  const { user, loading, hasAdminAccess, needsProfile, isRejected, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-lime border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change takes priority — user cannot navigate anywhere else
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (adminOnly && !hasAdminAccess) {
    return <Navigate to="/" replace />;
  }

  // Redirect to billing/shipping if user needs to fill profile or was rejected
  // But don't redirect if we're already on the billing page or if this route allows unverified access
  if (!allowUnverified && !hasAdminAccess && (needsProfile || isRejected) && location.pathname !== '/billing') {
    return <Navigate to="/billing" replace />;
  }

  return <>{children}</>;
}
